import { useCallback, useEffect, useState } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export type RsvpResponsesVersion = 'classic' | 'beta';

// localStorage keys — global to the admin user (not scoped per event), matching useColorScheme: one
// person opens many events back-to-back and we want their chosen view (and "seen the new page"
// state) to stick across all of them.
const VERSION_STORAGE_KEY = 'rsvp:answers:version';
const NEW_SEEN_STORAGE_KEY = 'rsvp:answers:new-results-seen';

// The redesign is the default for the 100% rollout: every admin lands on the new page first. They can
// opt back into the classic page via the banner, and that choice then persists here.
const DEFAULT_VERSION: RsvpResponsesVersion = 'beta';

const isValidVersion = (value: string | null): value is RsvpResponsesVersion => value === 'classic' || value === 'beta';

const readStoredVersion = (): RsvpResponsesVersion => {
  if (typeof window === 'undefined') return DEFAULT_VERSION;
  try {
    const stored = window.localStorage.getItem(VERSION_STORAGE_KEY);
    return isValidVersion(stored) ? stored : DEFAULT_VERSION;
  } catch {
    return DEFAULT_VERSION;
  }
};

// "Has this admin viewed the redesigned results page at least once?" Drives the one-time "New" nudge
// badge — once they've landed on the new page, the badge retires for good. Defaults false (not seen).
const readStoredSeen = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NEW_SEEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

// Module-level stores so every mounted consumer of this hook shares one value. Per-component
// `useState(readStored…)` gave each caller its own snapshot: marking the new page seen on the Answers
// page updated that instance + localStorage but left sibling instances (e.g. RsvpTabNav's "New"
// badge) reading the stale value until they remounted. A single external store fixes that — every
// subscriber re-renders on change, and the native `storage` event keeps other tabs in sync too (it
// only fires cross-tab, so same-tab sync rides on `notify` directly).
let currentVersion: RsvpResponsesVersion | null = null;
let currentSeen: boolean | null = null;
const listeners = new Set<() => void>();

const getVersionSnapshot = (): RsvpResponsesVersion => {
  if (currentVersion === null) currentVersion = readStoredVersion();
  return currentVersion;
};

const getSeenSnapshot = (): boolean => {
  if (currentSeen === null) currentSeen = readStoredSeen();
  return currentSeen;
};

const notify = () => listeners.forEach(listener => listener());

const setVersionGlobal = (next: RsvpResponsesVersion) => {
  currentVersion = next;
  try {
    window.localStorage.setItem(VERSION_STORAGE_KEY, next);
  } catch {
    // swallow — Safari private mode / quota; the in-memory store still drives this session.
  }
  notify();
};

// Record that the admin has now viewed the redesigned page. Idempotent: once seen, this is a no-op so
// repeat visits don't re-write storage or fire redundant notifies.
const markNewResultsSeenGlobal = () => {
  if (currentSeen === null) currentSeen = readStoredSeen();
  if (currentSeen) return;
  currentSeen = true;
  try {
    window.localStorage.setItem(NEW_SEEN_STORAGE_KEY, 'true');
  } catch {
    // swallow — Safari private mode / quota; the in-memory store still drives this session.
  }
  notify();
};

// Cross-tab: a `storage` event fires in *other* tabs when localStorage changes here. Re-read and
// notify so a second open tab's view + badge follow along. Same-tab updates never raise this event;
// those go through `notify` in the setters instead.
const handleStorage = (event: StorageEvent) => {
  if (event.key === VERSION_STORAGE_KEY) {
    const next = isValidVersion(event.newValue) ? event.newValue : DEFAULT_VERSION;
    if (next === currentVersion) return;
    currentVersion = next;
    notify();
  } else if (event.key === NEW_SEEN_STORAGE_KEY) {
    const next = event.newValue === 'true';
    if (next === currentSeen) return;
    currentSeen = next;
    notify();
  }
};

const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

type UseRsvpResponsesVersionReturn = Readonly<{
  // The version to render right now. Forced to 'classic' whenever the beta is unavailable (kill
  // switch off), so callers never need to special-case the flag when picking a page to render.
  version: RsvpResponsesVersion;
  setVersion: (version: RsvpResponsesVersion) => void;
  // Call when the redesigned page is actually viewed — retires the one-time "New" nudge badge.
  markNewResultsSeen: () => void;
  // Whether the redesign is available at all. When false, hide every "back to classic" / switch
  // affordance — the kill switch has retired the beta.
  isBetaAvailable: boolean;
  // Whether to show the "New" pill nudging admins toward the redesign: beta available and they
  // haven't viewed the new page yet. Tracks *having seen* the page, NOT the current selection — so
  // sitting on (or switching back to) classic never re-triggers it. Derived here so the dashboard
  // product row and the RSVP tab-nav badge share one definition.
  showRsvpNewPill: boolean;
}>;

/**
 * Resolves which RSVP Responses page to render: the redesigned ("beta") page or the classic one.
 *
 * Two inputs combine: the `rsvpResponsesBetaEnabled` remote flag (the kill switch — defaults on) and
 * a per-admin localStorage preference (defaults to beta — the redesign is the default view). When the
 * kill switch is off, the result is always 'classic' and `isBetaAvailable` is false, regardless of
 * any stored preference.
 */
export const useRsvpResponsesVersion = (): UseRsvpResponsesVersionReturn => {
  const [isBetaAvailable] = useIsFeatureEnabled('rsvpResponsesBetaEnabled');
  // Local mirrors of the shared module stores. Subscribing on mount means a change on one surface
  // (e.g. viewing the new page) immediately re-renders the others (page + tab-nav badge) without a
  // remount. (React 17 has no `useSyncExternalStore`, so we wire the subscription by hand.)
  const [storedVersion, setStoredVersion] = useState<RsvpResponsesVersion>(getVersionSnapshot);
  const [hasSeenNew, setHasSeenNew] = useState<boolean>(getSeenSnapshot);

  useEffect(() => {
    // Re-sync once on mount in case a store changed between the initial render and this effect.
    setStoredVersion(getVersionSnapshot());
    setHasSeenNew(getSeenSnapshot());
    return subscribe(() => {
      setStoredVersion(getVersionSnapshot());
      setHasSeenNew(getSeenSnapshot());
    });
  }, []);

  const setVersion = useCallback((next: RsvpResponsesVersion) => {
    setVersionGlobal(next);
  }, []);

  const markNewResultsSeen = useCallback(() => {
    markNewResultsSeenGlobal();
  }, []);

  const version: RsvpResponsesVersion = isBetaAvailable ? storedVersion : 'classic';
  const showRsvpNewPill = isBetaAvailable && !hasSeenNew;

  return { version, setVersion, markNewResultsSeen, isBetaAvailable, showRsvpNewPill };
};
